/**
 * 对话页（T06）可见部件元数据。
 *
 * 与 `conversations/componentMeta.ts` 同一约定：坐标是 390x844 画布上的绝对
 * 逻辑坐标（含顶部 24dp 状态栏偏移），文件同时是页面登记与 `npm run manifest`
 * 采集的数据源，避免两处坐标各写一套。
 *
 * 说明：`chat.composer.attachButton` / `chat.composer.sendButton` 两条与 T04
 * 示例清单里的 id、标签、坐标、props、notes 完全一致（T04 已按本页设计预留），
 * 因此不会产生重复的清单行。
 *
 * T23 追加：输入栏「生图」入口 + 待发送附件预览条（复用 T09 `AttachmentStrip`）、
 * 生图提示词弹层（`sheet` 动效）、消息内附件区与大图预览。附件区坐标换算直接复用
 * T09 `attachments/layout.ts` 的 `listImageLayout` / `listFileLayout`，两边不会漂移。
 */
import type { ComponentMeta } from '../../core/types';
import { LIST_FILE_HEIGHT, LIST_IMAGE_TILE, LIST_ROW_GAP, listFileLayout, listImageLayout } from '../attachments/layout';
import { buildComponentMeta, type ComponentMetaInput } from '../../ui/meta';

/** 页面内容区顶部：画布 y=24（状态栏安全区之下）。 */
export const PAGE_TOP = 24;
/** 消息列表区顶部（画布 y）。 */
export const MESSAGES_TOP = 80;
/** 消息列表区底部（画布 y，紧贴语音控制区）。 */
export const MESSAGES_BOTTOM = 636;
/** 输入栏顶部（画布 y）。 */
export const COMPOSER_TOP = 724;
/** 助手 / 用户气泡的声明宽度（画布 dp）。 */
export const BUBBLE_WIDTH = 326;
/** 消息气泡内附件区顶部：气泡标题下沿（气泡内边距之上）。 */
export const BUBBLE_ATTACH_OFFSET = 52;
/** 消息气泡内附件区左右内边距（与气泡正文一致）。 */
export const BUBBLE_ATTACH_PADDING = 12;

export const CHAT_COMPONENTS: ComponentMetaInput[] = [
  ...(['voiceBubble', 'voicePending', 'voiceDetails', 'voiceText', 'voiceProgress', 'closeVoiceDetails'] as const).map((name): ComponentMetaInput => ({
    id: `chat.message.assistant.${name}`, page: 'chat',
    role: name === 'voicePending' || name === 'voiceDetails' ? 'container' : name === 'voiceText' ? 'text' : name === 'voiceProgress' ? 'input' : 'button',
    label: ({ voiceBubble: '助手语音气泡', voicePending: '语音生成中', voiceDetails: '语音详情', voiceText: '语音回复文字', voiceProgress: '语音播放进度', closeVoiceDetails: '收起语音详情' })[name],
    layout: { x: 28, y: name === 'voiceText' || name === 'voiceDetails' ? 228 : name === 'voiceProgress' || name === 'closeVoiceDetails' ? 284 : 172, width: name === 'voicePending' ? 88 : name === 'voiceBubble' ? 166 : 200, height: 48 },
    zIndex: name === 'voiceProgress' || name === 'closeVoiceDetails' ? 8 : 6,
    notes: '运行期在assistant后插入消息id；生成中三点依次放大，完成后点击播放，长按500毫秒展开文字和播放进度；安卓对应语音消息和Slider',
  })),
  ...(['drawer', 'handle', 'backdrop', 'context', 'output', 'temperature', 'topP', 'thinking', 'stream'] as const).map((name, index): ComponentMetaInput => ({
    id: `chat.controls.${name}`, page: 'chat', role: name === 'drawer' ? 'modal' : name === 'handle' || name === 'backdrop' ? 'button' : 'input',
    label: ({ drawer: '会话参数菜单', handle: '展开会话菜单', backdrop: '关闭会话参数菜单', context: '最大上下文', output: '最大输出 token', temperature: '温度', topP: 'Top P', thinking: '思考等级', stream: '流式输出' })[name],
    layout: name === 'drawer' ? { x: 8, y: 80, width: 374, height: 600 } : name === 'backdrop' ? { x: 0, y: 24, width: 390, height: 744 } : { x: 24, y: name === 'handle' ? 680 : 204 + index * 44, width: 342, height: 44 },
    zIndex: name === 'backdrop' ? 69 : 70, props: ['value', 'open'], notes: '底部可上拉菜单，随内容堆叠自适应高度，最高 600dp 且不超过页头下方空间；随输入栏高度上移，收起时无阴影；参数自动保存，键盘 Escape 收起',
  })),
  {
    id: 'chat.controls.roleModel', page: 'chat', role: 'button', label: '角色模型配置',
    layout: { x: 25, y: 136, width: 340, height: 44 }, zIndex: 70,
    notes: '固定角色会话的模型配置入口；菜单内左右留白的全宽胶囊按钮，最小触摸高度 44dp；点击打开当前角色的模型配置',
  },
  {
    id: 'page.chat.root',
    page: 'chat',
    role: 'container',
    label: '对话页容器',
    layout: { x: 0, y: 24, width: 390, height: 744 },
    zIndex: 2,
    notes: '页面根容器（坐标含顶部 24dp 状态栏安全区偏移）；安卓用 Fragment 根 View',
  },
  {
    id: 'chat.header',
    page: 'chat',
    role: 'container',
    label: '对话页头',
    layout: { x: 0, y: 24, width: 390, height: 56 },
    zIndex: 3,
    notes: '返回、会话标题、新对话、会话列表、图库和联网搜索；安卓用 MaterialToolbar',
  },
  {
    id: 'chat.header.backButton',
    page: 'chat',
    role: 'button',
    label: '返回',
    layout: { x: 8, y: 30, width: 44, height: 44 },
    zIndex: 4,
    notes: '返回会话列表；安卓用 Toolbar navigationIcon',
  },
  {
    id: 'chat.header.newConversation', page: 'chat', role: 'button', label: '开启新对话',
    layout: { x: 190, y: 30, width: 44, height: 44 }, zIndex: 6,
    notes: '消息框加号；为当前角色创建独立会话，继承模型与参数；44dp 点击区域',
  },
  {
    id: 'chat.header.sessions', page: 'chat', role: 'button', label: '打开会话列表',
    layout: { x: 238, y: 30, width: 44, height: 44 }, zIndex: 6,
    notes: '三个方框与横线；打开当前角色的历史会话，44dp 点击区域',
  },
  {
    id: 'chat.sessions.dialog', page: 'chat', role: 'modal', label: '角色会话列表',
    layout: { x: 10, y: 300, width: 370, height: 456 }, zIndex: 91,
    notes: '当前角色独立会话列表；高于参数抽屉，支持 Escape 关闭与键盘焦点循环',
  },
  {
    id: 'chat.sessions.close', page: 'chat', role: 'button', label: '关闭会话列表',
    layout: { x: 324, y: 308, width: 44, height: 44 }, zIndex: 92,
    notes: '关闭角色会话列表并返回触发按钮',
  },
  {
    id: 'chat.header.galleryButton',
    page: 'chat',
    role: 'button',
    label: '图库',
    layout: { x: 286, y: 30, width: 44, height: 44 },
    zIndex: 6,
    notes: '对话页头部「图库」入口；点击进入 #/gallery（T22）；安卓用 ImageButton',
  },
  {
    id: 'chat.header.title',
    page: 'chat',
    role: 'text',
    label: '会话标题',
    layout: { x: 60, y: 36, width: 126, height: 32 },
    zIndex: 4,
    notes: '无会话时显示「对话」；单行省略，右侧为四个图标按钮留位',
  },
  {
    id: 'chat.header.modelSelect',
    page: 'chat',
    role: 'input',
    label: '模型选择',
    layout: { x: 24, y: 168, width: 342, height: 44 },
    zIndex: 7,
    props: ['value', 'disabled', 'options'],
    notes: '选择当前提供商的全部模型；模型ID与提供商一起保存到会话',
  },
  {
    id: 'chat.header.providerSelect', page: 'chat', role: 'input', label: '提供商选择',
    layout: { x: 24, y: 112, width: 342, height: 44 }, zIndex: 7,
    props: ['value', 'disabled', 'options'], notes: '官方模型与所有启用提供商；切换后加载对应模型列表',
  },
  {
    id: 'chat.header.refreshModels', page: 'chat', role: 'button', label: '刷新模型列表',
    layout: { x: 24, y: 224, width: 342, height: 44 }, zIndex: 7,
    props: ['disabled', 'loading'], notes: '重新读取当前提供商模型，失败后可重试；生成过程中禁止切换',
  },
  {
    id: 'chat.messages',
    page: 'chat',
    role: 'list',
    label: '消息列表',
    layout: { x: 0, y: MESSAGES_TOP, width: 390, height: MESSAGES_BOTTOM - MESSAGES_TOP },
    zIndex: 3,
    props: ['items', 'streaming'],
    notes: '可滚动消息流；安卓用 RecyclerView（reverseLayout=false，堆栈自底向上）',
  },
  {
    id: 'chat.empty',
    page: 'chat',
    role: 'container',
    label: '对话空状态',
    layout: { x: 0, y: 320, width: 390, height: 140 },
    zIndex: 3,
    notes: '没有活动会话时显示引导',
  },
  {
    id: 'chat.empty.title',
    page: 'chat',
    role: 'text',
    label: '空状态主文案',
    layout: { x: 0, y: 336, width: 390, height: 32 },
    zIndex: 4,
    notes: '居中',
  },
  {
    id: 'chat.empty.hint',
    page: 'chat',
    role: 'text',
    label: '空状态说明',
    layout: { x: 0, y: 372, width: 390, height: 24 },
    zIndex: 4,
    notes: '居中，次要文本色',
  },
  {
    id: 'chat.empty.actionButton',
    page: 'chat',
    role: 'button',
    label: '去会话列表新建',
    layout: { x: 123, y: 412, width: 144, height: 44 },
    zIndex: 4,
    notes: '空状态主按钮',
  },
  {
    id: 'chat.providerNotice',
    page: 'chat',
    role: 'container',
    label: '无可用模型提供商提示条',
    layout: { x: 0, y: 80, width: 326, height: 44 },
    zIndex: 6,
    notes: '没有启用的 provider 时显示，避免直接崩溃',
  },
  {
    id: 'chat.providerNotice.message',
    page: 'chat',
    role: 'text',
    label: '提供商提示文案',
    layout: { x: 16, y: 84, width: 202, height: 36 },
    zIndex: 7,
    notes: '居中两行',
  },
  {
    id: 'chat.providerNotice.actionButton',
    page: 'chat',
    role: 'button',
    label: '去设置页配置',
    layout: { x: 226, y: 80, width: 100, height: 44 },
    zIndex: 7,
    notes: '跳转 /settings（T15 配置页）',
  },
  {
    id: 'chat.errorBanner',
    page: 'chat',
    role: 'container',
    label: '错误提示条',
    layout: { x: 8, y: 564, width: 374, height: 64 },
    zIndex: 6,
    notes: '发送失败 / 模型未配置时显示；位于收起的会话菜单上方，自适应文字高度，长消息可滚动',
  },
  {
    id: 'chat.errorBanner.message',
    page: 'chat',
    role: 'text',
    label: '错误信息',
    layout: { x: 20, y: 576, width: 350, height: 40 },
    zIndex: 7,
    notes: '在错误提示条内自然换行，长模型名与错误消息可滚动查看',
  },
  {
    id: 'chat.message.user',
    page: 'chat',
    role: 'container',
    label: '用户消息气泡（模板）',
    layout: { x: 48, y: 96, width: BUBBLE_WIDTH, height: 64 },
    zIndex: 4,
    props: ['content', 'status'],
    notes: '右对齐、主色底；运行时登记为 chat.message.user.<消息id>',
  },
  {
    id: 'chat.message.assistant',
    page: 'chat',
    role: 'container',
    label: '助手消息气泡（模板）',
    layout: { x: 16, y: 172, width: BUBBLE_WIDTH, height: 64 },
    zIndex: 4,
    props: ['content', 'reasoning', 'status'],
    notes: '左对齐、次级容器底色；运行时登记为 chat.message.assistant.<消息id>',
  },
  {
    id: 'chat.message.text',
    page: 'chat',
    role: 'text',
    label: '消息正文',
    layout: { x: 30, y: 108, width: 302, height: 40 },
    zIndex: 5,
    notes: '流式增量追加；安卓用 TextView / Compose Text',
  },
  {
    id: 'chat.message.footer',
    page: 'chat',
    role: 'container',
    label: '消息气泡外操作栏（模板）',
    layout: { x: 16, y: 237, width: 105, height: 25.5 },
    zIndex: 5,
    props: ['status'],
    notes: '已完成消息或含部分回复的失败消息显示；气泡之外下方 1dp，操作栏最小高度 25.5dp，子项间距 1dp；真实坐标随消息高度流式变化，用户消息右对齐；安卓用气泡同级 LinearLayout',
  },
  {
    id: 'chat.message.timestamp',
    page: 'chat',
    role: 'text',
    label: '消息生成时间（模板）',
    layout: { x: 16, y: 239.75, width: 50, height: 20 },
    zIndex: 6,
    props: ['createdAt'],
    notes: '气泡外操作栏中的 HH:mm 时间，垂直居中，右外边距 2dp，再接操作栏 1dp 间距；完整日期为悬停提示，颜色跟随主题及用户图标配色；安卓用 TextView',
  },
  {
    id: 'chat.message.copyButton',
    page: 'chat',
    role: 'button',
    label: '复制消息（模板）',
    layout: { x: 69, y: 237, width: 25.5, height: 25.5 },
    zIndex: 6,
    props: ['copyState'],
    notes: '气泡外操作栏，15dp 图标居中于 25.5dp 点击区域，按钮间距 1dp，图标之间可见距离 11.5dp，图标距气泡下缘 6.25dp；复制消息正文，图片消息复制图片地址，成功显示对勾，颜色跟随主题及用户图标配色；安卓用 ImageButton',
  },
  {
    id: 'chat.message.deleteButton',
    page: 'chat',
    role: 'button',
    label: '删除消息（模板）',
    layout: { x: 95.5, y: 237, width: 25.5, height: 25.5 },
    zIndex: 6,
    notes: '气泡外操作栏，15dp 图标居中于 25.5dp 点击区域，按钮间距 1dp，图标之间可见距离 11.5dp，图标距气泡下缘 6.25dp；只删除选中的一条消息，颜色跟随主题及用户图标配色；安卓用 ImageButton',
  },
  {
    id: 'chat.message.imageSending',
    page: 'chat',
    role: 'text',
    label: '图片发送中（模板）',
    layout: { x: 28, y: 208, width: 302, height: 18 },
    zIndex: 5,
    props: ['imageJob', 'imageSending'],
    notes: '直接生图与角色自动生图进行中显示在气泡内部下方，正文和附件之后；完成或失败时移除，实际位置随内容高度变化；安卓用状态 TextView',
  },
  {
    id: 'chat.message.reasoning',
    page: 'chat',
    role: 'container',
    label: '思考过程折叠区',
    layout: { x: 28, y: 104, width: 302, height: 40 },
    zIndex: 5,
    props: ['expanded', 'reasoning'],
    notes: '仅助手消息有 reasoning 时渲染；默认折叠，点击展开',
  },
  {
    id: 'chat.message.error',
    page: 'chat',
    role: 'text',
    label: '消息错误态',
    layout: { x: 28, y: 104, width: 302, height: 40 },
    zIndex: 5,
    notes: 'status=error 时显示错误色文案',
  },
  {
    id: 'chat.composer.bar',
    page: 'chat',
    role: 'container',
    label: '底部输入栏',
    layout: { x: 0, y: 724, width: 390, height: 44 },
    zIndex: 3,
    notes: '空输入高度 44dp；随文字向上增长，底部固定，输入框上下各留 4dp；安卓用 ConstraintLayout 底部输入区',
  },
  {
    id: 'chat.composer.input',
    page: 'chat',
    role: 'input',
    label: '消息输入框',
    layout: { x: 60, y: 728, width: 270, height: 36 },
    zIndex: 4,
    props: ['value', 'placeholder', 'multiline'],
    notes: '随文字换行向上自适应，高度 36–160dp，超过上限在框内滚动；删除、发送、语音转写均重算高度；安卓用 EditText（inputType=textMultiLine）',
  },
  {
    id: 'chat.composer.attachButton',
    page: 'chat',
    role: 'button',
    label: '上传图片/文件',
    layout: { x: 8, y: 724, width: 44, height: 44 },
    zIndex: 16,
    props: ['disabled'],
    notes: '附件选择，安卓用 ImageButton + 系统文件选择器',
  },
  {
    id: 'chat.composer.sendButton',
    page: 'chat',
    role: 'button',
    label: '发送',
    layout: { x: 338, y: 724, width: 44, height: 44 },
    zIndex: 16,
    props: ['disabled', 'loading'],
    notes: '发送/停止切换，带 spring 回弹；安卓用 MaterialButton',
  },
  {
    id: 'chat.composer.attachments',
    page: 'chat',
    role: 'list',
    label: '待发送附件预览条',
    layout: { x: 0, y: 548, width: 390, height: 112 },
    zIndex: 12,
    props: ['items'],
    notes: '输入栏上方的已选附件缩略图（复用 T09 AttachmentStrip，含逐个移除）；安卓用横向 RecyclerView',
  },
  {
    id: 'chat.image.galleryNotice',
    page: 'chat',
    role: 'button',
    label: '已存入图库提示',
    layout: { x: 12, y: 88, width: 366, height: 36 },
    zIndex: 13,
    notes: '生图成功后出现；点击跳转 #/gallery；安卓用 Snackbar + action',
  },
  {
    id: 'chat.message.attachments',
    page: 'chat',
    role: 'list',
    label: '消息内附件区（模板）',
    layout: { x: 28, y: 148, width: 302, height: 100 },
    zIndex: 5,
    props: ['items'],
    notes: '图片缩略图 + 文件卡片；运行时登记为 chat.message.<role>.<消息id>.attachments；安卓用 LinearLayout',
  },
  {
    id: 'chat.message.attachment.image',
    page: 'chat',
    role: 'button',
    label: '消息内图片缩略图（模板）',
    layout: { x: 28, y: 148, width: LIST_IMAGE_TILE, height: LIST_IMAGE_TILE },
    zIndex: 6,
    notes: '点击打开页面层大图预览；安卓用 ImageButton + Dialog（与 T09 同尺寸 72dp）',
  },
  {
    id: 'chat.message.attachment.file',
    page: 'chat',
    role: 'button',
    label: '消息内文件卡片（模板）',
    layout: { x: 28, y: 228, width: 302, height: LIST_FILE_HEIGHT },
    zIndex: 6,
    props: ['download'],
    notes: '显示文件名 + 大小，可下载 / 另存；安卓用 MaterialCardView + ACTION_CREATE_DOCUMENT',
  },
  {
    id: 'chat.imagePreview.backdrop',
    page: 'chat',
    role: 'modal',
    label: '消息图片大图预览遮罩',
    layout: { x: 0, y: 24, width: 390, height: 744 },
    zIndex: 40,
    notes: '点击遮罩关闭；安卓用 Dialog + 全屏遮罩',
  },
  {
    id: 'chat.imagePreview.image',
    page: 'chat',
    role: 'icon',
    label: '消息图片大图',
    layout: { x: 16, y: 104, width: 358, height: 552 },
    zIndex: 41,
    notes: '按 contain 适配；安卓用 ImageView + adjustViewBounds',
  },
  {
    id: 'chat.imagePreview.closeButton',
    page: 'chat',
    role: 'button',
    label: '关闭消息图片预览',
    layout: { x: 330, y: 112, width: 44, height: 44 },
    zIndex: 42,
    notes: '触摸目标 44x44dp',
  },
  {
    id: 'chat.controls.presetSelect',
    page: 'chat',
    role: 'input',
    label: '角色卡选择',
    layout: { x: 24, y: 326, width: 342, height: 44 },
    zIndex: 70,
    props: ['value', 'disabled', 'options'],
    notes: '位于会话菜单模型与语音选项之间；无角色会话可首次绑定，绑定角色后锁定并禁止切换或清除',
  },
  {
    id: 'chat.message.contextNotice',
    page: 'chat',
    role: 'container',
    label: '注入上下文信息条',
    layout: { x: 28, y: 104, width: 302, height: 44 },
    zIndex: 5,
    props: ['expanded'],
    notes: '助手消息下方「已注入上下文」提示条；运行期登记为 <消息id>.context；安卓对应可折叠 CardView',
  },
  {
    id: 'chat.message.contextNotice.toggle',
    page: 'chat',
    role: 'button',
    label: '展开/收起注入上下文',
    layout: { x: 28, y: 104, width: 302, height: 44 },
    zIndex: 6,
    props: ['expanded'],
    notes: '点击展开 / 收起注入来源明细；可点高度不小于 44dp；安卓对应 MaterialButton',
  },
  {
    id: 'chat.message.contextNotice.text',
    page: 'chat',
    role: 'text',
    label: '注入来源明细',
    layout: { x: 28, y: 148, width: 302, height: 32 },
    zIndex: 5,
    notes: '按角色卡 / 世界书 / 记忆 / 搜索顺序逐行显示来源与计数；安卓对应 TextView',
  },
  {
    id: 'chat.statusHint',
    page: 'chat',
    role: 'container',
    label: '生成状态提示',
    layout: { x: 0, y: 700, width: 390, height: 20 },
    zIndex: 6,
    notes: '取消生成 / 状态提示的容器',
  },
  {
    id: 'chat.statusHint.message',
    page: 'chat',
    role: 'text',
    label: '生成状态文案',
    layout: { x: 16, y: 700, width: 358, height: 20 },
    zIndex: 7,
    notes: '如「已停止生成」',
  },
];

const COMPONENT_INDEX = new Map<string, ComponentMetaInput>(
  CHAT_COMPONENTS.map((meta) => [meta.id, meta]),
);

/** 按 id 取声明式元数据输入；未登记直接抛错，避免清单静默缺项。 */
export function chatMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[chat] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局（页面内联定位与清单坐标共用一份数据）。 */
export function chatLayout(id: string): ComponentMetaInput['layout'] {
  return chatMeta(id).layout;
}

/** 按 id 构造完整 ComponentMeta，供 `useComponentMeta()` 登记。 */
export function chatComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(chatMeta(id));
}

/**
 * 第 index 条消息的声明布局。
 *
 * 消息高度随内容变化，这里给出「清单用」的声明锚点（模板行高 76dp），
 * 真实位置由列表流式布局决定，`notes` 里已注明。
 */
export function messageBubbleLayout(
  role: 'user' | 'assistant',
  index: number,
): ComponentMetaInput['layout'] {
  const y = 96 + index * 76;
  return role === 'user'
    ? { x: 48, y, width: BUBBLE_WIDTH, height: 64 }
    : { x: 16, y, width: BUBBLE_WIDTH, height: 64 };
}

/** 待发送附件预览条（输入栏上方）的声明布局。 */
export function composerAttachmentsLayout(): ComponentMetaInput['layout'] {
  return chatLayout('chat.composer.attachments');
}

/**
 * 消息气泡内附件区的声明布局。
 *
 * 高度按「图片一行 72dp + 文件卡片 52dp / 张」累计，与 T09 的
 * `listImageLayout` / `listFileLayout` 完全一致；真实气泡高度仍由内容决定，
 * 这里给出的是清单位置（与 `chat.message.text` 同一策略）。
 */
export function messageAttachmentsLayout(
  bubble: ComponentMetaInput['layout'],
  imageCount: number,
  fileCount: number,
): ComponentMetaInput['layout'] {
  const imagesHeight = imageCount > 0 ? LIST_IMAGE_TILE : 0;
  const filesHeight =
    fileCount > 0 ? fileCount * LIST_FILE_HEIGHT + (fileCount - 1) * LIST_ROW_GAP : 0;
  const gap = imageCount > 0 && fileCount > 0 ? LIST_ROW_GAP : 0;
  return {
    x: bubble.x + BUBBLE_ATTACH_PADDING,
    y: bubble.y + BUBBLE_ATTACH_OFFSET,
    width: bubble.width - BUBBLE_ATTACH_PADDING * 2,
    height: Math.max(1, imagesHeight + gap + filesHeight),
  };
}

/** 消息内第 index 张图片的声明布局（换算自附件区）。 */
export function messageAttachmentImageLayout(
  area: ComponentMetaInput['layout'],
  index: number,
): ComponentMetaInput['layout'] {
  return listImageLayout(area, index);
}

/** 消息内第 index 个文件卡片的声明布局（接在图片行之下）。 */
export function messageAttachmentFileLayout(
  area: ComponentMetaInput['layout'],
  index: number,
  imageCount: number,
): ComponentMetaInput['layout'] {
  return listFileLayout(area, index, imageCount);
}
